import { LinearProgress } from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { API } from '~/api';
import { FeedbackStatus, FeedbackType } from '~/api/api.declaration';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { FeedbackService } from '~/service/service.feedback';
import { UIService } from '~/service/service.ui';
import { Enum } from '~/shared/fields';
import { useService } from '~/shared/service/service.base';
import { FeedbackDrawer } from '~/view/feedback/feedback.drawer';
import { FieldSelect } from '~/view/fields/field.select';
import { DateTimeColumn, LinkColumn, useGridColumns } from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';
import { Text } from '~/view/view.box';
import { LinkView } from '~/view/view.link';

export const FeedbackListPage = observer(() => {
	const [feedbackSvc, ui, auth] = useService(FeedbackService, UIService, AuthService);
	const columns = useGridColumns<API.Feedback>(() => [
		DateTimeColumn({ field: 'createdAt', headerName: 'CreatedAt' }),
		{
			field: 'status',
			headerName: 'Status',
			type: 'singleSelect',
			valueOptions: Enum.keys(FeedbackStatus),
			width: 120,
		},
		LinkColumn({
			field: 'createdByName',
			getLink: row => AppRoutes.UserEdit.buildPath({ id: row.createdBy }),
			headerName: 'By',
			sortable: false,
		}),
		{ field: 'type', headerName: 'Type', width: 250 },
		{ field: 'rate', headerName: 'Rate', width: 70 },
		{ field: 'message', flex: 1, headerName: 'Subject', renderCell: renderSubjectCell },
	]);

	ui.usePageTitle('Feedback');
	useEffect(() => feedbackSvc.loadAndSubscribe(), [feedbackSvc]);

	return (
		<>
			<FeedbackDrawer model={feedbackSvc.drawer} />
			<GridContainer async={feedbackSvc.async}>
				<DataGrid
					initialState={feedbackSvc.gridState.initial()}
					loading={feedbackSvc.async.loading}
					rowCount={feedbackSvc.totalCount}
					rows={feedbackSvc.items.slice()}
					columns={columns}
					paginationMode="server"
					sortingMode="server"
					filterMode="server"
					onStateChange={feedbackSvc.gridState.onStateChange}
					onFilterModelChange={feedbackSvc.changeFilterModel}
					disableColumnFilter
					onRowClick={params => feedbackSvc.drawer.setSelectedItem(params.row)}
					slots={{
						loadingOverlay: LinearProgress,
						noRowsOverlay: () => <GridNoRows>No feedbacks found</GridNoRows>,
						pagination: MuiGridPagination,
						toolbar: () => (
							<GridToolbarView
								onReload={feedbackSvc.reload}
								gridState={feedbackSvc.gridState}
								exportEnabled={auth.can('feedback', 'EXPORT')}
							>
								<FieldSelect
									fullWidth={false}
									minWidth={120}
									label="Status"
									nullLabel="All"
									field={feedbackSvc.filterFieldsForm.$status}
									items={Enum.keys(FeedbackStatus).map(value => ({ value, label: value }))}
									valueProp="value"
									labelProp="label"
								/>
								<FieldSelect
									fullWidth={false}
									minWidth={200}
									label="Type"
									nullLabel="All"
									field={feedbackSvc.filterFieldsForm.$type}
									items={Enum.keys(FeedbackType).map(value => ({ value, label: value }))}
									valueProp="value"
									labelProp="label"
								/>
							</GridToolbarView>
						),
					}}
				/>
			</GridContainer>
		</>
	);
});

function renderSubjectCell(params: GridRenderCellParams<API.Feedback>) {
	const row = params.row as API.Feedback;
	if (row.type === 'POST_COMMENT_COMPLAINT') {
		return (
			<LinkView
				to={AppRoutes.PostEditCommentList.buildPath(
					{ id: row.postId ?? 0 },
					{ commentId: row.postCommentId ?? 0 },
				)}
				target="_blank"
			>
				[{row.complaint}] {row.postCommentAuthor}: {row.postCommentText}
			</LinkView>
		);
	} else if (row.type === 'POST_COMPLAINT') {
		return (
			<LinkView to={AppRoutes.PostEdit.buildPath({ id: row.postId ?? 0 })} target="_blank">
				[{row.complaint}] Post by {row.postAuthor}: {row.postText}
			</LinkView>
		);
	} else if (row.type === 'PHOTO_COMPLAINT') {
		return (
			<LinkView to={AppRoutes.FragranceEdit.buildPath({ id: row.fragranceId ?? 0 })} target="_blank">
				[{row.complaint}] Post photo by {row.postAuthor}: {row.postText}
			</LinkView>
		);
	} else if (row.type === 'REVIEW_COMPLAINT') {
		return (
			<LinkView to={AppRoutes.FragranceEdit.buildPath({ id: row.fragranceId ?? 0 })} target="_blank">
				[{row.complaint}] Fragrance review for {row.fragranceId}
			</LinkView>
		);
	} else if (row.type === 'USER_COMPLAINT') {
		return (
			<LinkView to={AppRoutes.UserEdit.buildPath({ id: row?.userId ?? 0 })} target="_blank">
				[{row.complaint}] User {row.userName}: {row.message}
			</LinkView>
		);
	} else if (row.type === 'FRAG_SUGGESTION') {
		return <Text variant="body2">{row.message}</Text>;
	} else {
		return (
			<Text variant="body2" noWrap>
				{row.message}
			</Text>
		);
	}
}
